//# sourceMappingURL=assoc.mjs.map
/// <reference types="./assoc.d.mts" />
import * as $list from "../../gleam_stdlib/gleam/list.mjs";
import * as $option from "../../gleam_stdlib/gleam/option.mjs";
import { None, Some } from "../../gleam_stdlib/gleam/option.mjs";
import * as $pair from "../../gleam_stdlib/gleam/pair.mjs";
import { Ok, Error, toList, Empty as $Empty, prepend as listPrepend, isEqual } from "../gleam.mjs";

/**
 * Returns a new empty assoc.
 *
 * ```gleam
 * assoc.new()
 * // []
 * ```
 */
export function new$() {
  return $list.new$();
}

/**
 * Push an element at the end of the assoc. If the key is already bind, a
 * new element with the same key will be added. If you want to replace an
 * existing item, use `assoc.set` instead.
 *
 * Runs in linear time.
 *
 * ```gleam
 * assoc.push([#("first", 1)], "second", 2)
 * // [#("first", 1), #("second", 2)]
 *
 * assoc.push([#("first", 1)], "first", 2)
 * // [#("first", 1), #("first", 2)]
 * ```
 */
export function push(assoc, key, value) {
  return $list.append(assoc, toList([[key, value]]));
}

/**
 * Merge two lists in one. Alias for `list.append`.
 */
export function append(assoc, assoc2) {
  return $list.append(assoc, assoc2);
}

/**
 * Flatten an association list. Alias for `list.flatten`.
 */
export function flatten(assocs) {
  return $list.flatten(assocs);
}

/**
 * Fold an association list. Alias for `list.fold`.
 */
export function fold(assocs, initial_value, folder) {
  return $list.fold(assocs, initial_value, folder);
}

/**
 * Reverses a list and prepends it to another list.
 * This function runs in linear time, proportional to the lenght of the list
 * to prepend.
 * 
 * @ignore
 */
function reverse_and_prepend(loop$prefix, loop$suffix) {
  while (true) {
    let prefix = loop$prefix;
    let suffix = loop$suffix;
    if (prefix instanceof $Empty) {
      return suffix;
    } else {
      let first = prefix.head;
      let rest = prefix.tail;
      loop$prefix = rest;
      loop$suffix = listPrepend(first, suffix);
    }
  }
}

function do_set(loop$assoc, loop$key, loop$value, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let value = loop$value;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(listPrepend([key, value], res));
    } else {
      let item = assoc.head;
      if (!isEqual(key, item[0])) {
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$value = value;
        loop$res = listPrepend(item, res);
      } else {
        let rest = assoc.tail;
        return reverse_and_prepend(res, listPrepend([key, value], rest));
      }
    }
  }
}

/**
 * Set an element in the assoc. If the element already exists, it is replaced,
 * otherwise it will be added at the end of the list. If you want to keep
 * existing items, use `assoc.append` instead.
 *
 * ```gleam
 * assoc.set([#("first", 1)], "second", 2)
 * // [#("first", 1), #("second", 2)]
 *
 * assoc.set([#("first", 1)], "first", 2)
 * // [#("first", 2)]
 * ```
 */
export function set(assoc, key, value) {
  return do_set(assoc, key, value, toList([]));
}

/**
 * Set an element in the assoc, if the value exists.
 *
 * ```gleam
 * assoc.optional_set([#("first", 1)], "second", Some(2))
 * // [#("first", 1), #("second", 2)]
 *
 * assoc.optional_set([#("first", 1)], "second", None)
 * // [#("first", 1)]
 * ```
 */
export function optional_set(assoc, key, value) {
  if (value instanceof Some) {
    let value$1 = value[0];
    return set(assoc, key, value$1);
  } else {
    return assoc;
  }
}

/**
 * Get the first element corresponding to the key in the assoc.
 * Runs in linear time.
 *
 * ```gleam
 * assoc.get([#("first", 1)], "first")
 * // Ok(1)
 *
 * assoc.get([#("first", 1), #("first", 2)], "first")
 * // Ok(1)
 *
 * assoc.get([], "anything")
 * // Error(Nil)
 * ```
 */
export function get(loop$assoc, loop$key) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    if (assoc instanceof $Empty) {
      return new Error(undefined);
    } else {
      let old = assoc.head[0];
      if (isEqual(key, old)) {
        let value = assoc.head[1];
        return new Ok(value);
      } else {
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
      }
    }
  }
}

/**
 * Get the first element corresponding to the key in the assoc, or returns
 * no data. Runs in linear time.
 *
 * It is extremely similar to `get`, but can be used in conjunction with
 * `gleam/option` module.
 *
 * ```gleam
 * assoc.optional_get([#("first", 1)], "first")
 * // Some(1)
 *
 * assoc.optional_get([#("first", 1), #("first", 2)], "first")
 * // Some(1)
 *
 * assoc.optional_get([], "anything")
 * // None
 * ```
 */
export function optional_get(assoc, key) {
  let _pipe = get(assoc, key);
  return $option.from_result(_pipe);
}

function do_all(loop$assoc, loop$key, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(res);
    } else {
      let old = assoc.head[0];
      if (isEqual(key, old)) {
        let rest = assoc.tail;
        let value = assoc.head[1];
        loop$assoc = rest;
        loop$key = key;
        loop$res = listPrepend(value, res);
      } else {
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$res = res;
      }
    }
  }
}

/**
 * Get all elements corresponding to the key in the assoc.
 *
 * ```gleam
 * assoc.all([#("first", 1), #("second", 2)], "first")
 * // [1]
 *
 * assoc.all([#("first", 1), #("first", 2)], "first")
 * // [1, 2]
 *
 * assoc.all([#("first", 1), #("second", 2)], "none")
 * // []
 * ```
 */
export function all(assoc, key) {
  return do_all(assoc, key, toList([]));
}

/**
 * Map over an assoc. Alias for `list.map`.
 */
export function map(assoc, mapper) {
  return $list.map(assoc, mapper);
}

/**
 * Map over the keys of an assoc. Alias for
 * `list.map(assoc, pair.map_first(_, mapper))`.
 */
export function map_keys(assoc, mapper) {
  return $list.map(
    assoc,
    (_capture) => { return $pair.map_first(_capture, mapper); },
  );
}

/**
 * Map over the values of an assoc. Alias for
 * `list.map(assoc, pair.map_second(_, mapper))`.
 */
export function map_values(assoc, mapper) {
  return $list.map(
    assoc,
    (_capture) => { return $pair.map_second(_capture, mapper); },
  );
}

function do_upsert(loop$assoc, loop$key, loop$mapper, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let mapper = loop$mapper;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(listPrepend([key, mapper(new None())], res));
    } else {
      let item = assoc.head;
      if (isEqual(item[0], key)) {
        let rest = assoc.tail;
        return reverse_and_prepend(
          res,
          listPrepend([key, mapper(new Some(item[1]))], rest),
        );
      } else {
        let item = assoc.head;
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$mapper = mapper;
        loop$res = listPrepend(item, res);
      }
    }
  }
}

/**
 * Upsert a key in the assoc. If the item can't be found, it will be inserted
 * at the end of the assoc.
 *
 * ```gleam
 * use item <- assoc.upsert([#("first", 1), #("second", 2)], "first")
 * option.unwrap(item, 0) + 1
 * // [#("first", 2), #("second", 2)]
 *
 * use item <- assoc.upsert([#("first", 1), #("first", 2)], "first")
 * option.unwrap(item, 0) + 1
 * // [#("first", 2), #("first", 2)]
 *
 * use item <- assoc.upsert([#("first", 1), #("second", 2)], "none")
 * option.unwrap(item, 0) + 1
 * // [#("first", 1), #("second", 2), #("none", 1)]
 * ```
 */
export function upsert(assoc, key, mapper) {
  return do_upsert(assoc, key, mapper, toList([]));
}

function do_remove(loop$assoc, loop$key, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(res);
    } else {
      let old = assoc.head[0];
      if (isEqual(old, key)) {
        let rest = assoc.tail;
        return reverse_and_prepend(res, rest);
      } else {
        let item = assoc.head;
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$res = listPrepend(item, res);
      }
    }
  }
}

/**
 * Remove the first matching element of the assoc.
 *
 * ```gleam
 * assoc.remove([#("first", 1), #("second", 2)], "first")
 * // [#("second", 2)]
 *
 * assoc.remove([#("first", 1), #("first", 2)], "first")
 * // [#("first", 2)]
 *
 * assoc.remove([#("first", 1), #("second", 2)], "none")
 * // [#("first", 1), #("second", 2)]
 * ```
 */
export function remove(assoc, key) {
  return do_remove(assoc, key, toList([]));
}

function do_remove_all(loop$assoc, loop$key, loop$res) {
  while (true) {
    let assoc = loop$assoc;
    let key = loop$key;
    let res = loop$res;
    if (assoc instanceof $Empty) {
      return $list.reverse(res);
    } else {
      let old = assoc.head[0];
      if (isEqual(old, key)) {
        let rest = assoc.tail;
        loop$assoc = rest;
        loop$key = key;
        loop$res = res;
      } else {
        let item = assoc.head;
        let rest = assoc.tail;
        return do_remove(rest, key, listPrepend(item, res));
      }
    }
  }
}

/**
 * Remove all matching element of the assoc.
 *
 * ```gleam
 * assoc.remove_all([#("first", 1), #("second", 2)], "first")
 * // [#("second", 2)]
 *
 * assoc.remove_all([#("first", 1), #("first", 2)], "first")
 * // []
 *
 * assoc.remove_all([#("first", 1), #("second", 2)], "none")
 * // [#("first", 1), #("second", 2)]
 * ```
 */
export function remove_all(assoc, key) {
  return do_remove_all(assoc, key, toList([]));
}
